import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaCodeBranch,
  FaPlug,
  FaSearch,
  FaTag,
  FaClock,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaLayerGroup,
  FaChevronDown,
  FaChevronUp,
  FaFile,
  FaArrowLeft,
} from "react-icons/fa";
import "../Form/CreateCustomerPage.css";

/* ─────────────────────────────────────────────────────────
   DATE / TIME HELPERS
   We parse the ISO string from the server and display
   the exact date/time that was stored (UTC-aware display).
   If the string already contains an offset (e.g. +05:30)
   the browser will honour it; otherwise we treat it as-is.
──────────────────────────────────────────────────────────── */
const parseDT = (ds) => {
  if (!ds) return null;
  const d = new Date(ds);
  return isNaN(d.getTime()) ? null : d;
};

/** Returns DD/MM/YYYY using LOCAL browser timezone (matches system clock) */
const formatDate = (ds) => {
  const d = parseDT(ds);
  if (!d) return "N/A";
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1
  ).padStart(2, "0")}/${d.getFullYear()}`;
};

/** Returns H:MM AM/PM using LOCAL browser timezone (matches system clock) */
const formatTime = (ds) => {
  const d = parseDT(ds);
  if (!d) return "N/A";
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

/* ─────────────────────────────────────────────────────────
   PILL BADGE
──────────────────────────────────────────────────────────── */
const Pill = ({ children, bg, color, style = {} }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 20,
      background: bg,
      color,
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </span>
);

/* ─────────────────────────────────────────────────────────
   DATE + TIME DISPLAY (inline pair)
──────────────────────────────────────────────────────────── */
const DateTimeBadges = ({ ds }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "#F0FDF4",
        color: "#15803D",
        padding: "3px 8px",
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      <FaCalendarAlt style={{ fontSize: 9 }} />
      {formatDate(ds)}
    </span>
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "#EFF6FF",
        color: "#1D4ED8",
        padding: "3px 8px",
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      <FaClock style={{ fontSize: 9 }} />
      {formatTime(ds)}
    </span>
  </div>
);

/* ─────────────────────────────────────────────────────────
   PAGES EXPANDABLE CELL
   "X Pages ▼"  →  click → module/page/price table
──────────────────────────────────────────────────────────── */
const PagesExpand = ({ planFeatures, planPages }) => {
  const [open, setOpen] = useState(false);

  // flatten all pages across modules
  const allPages = [];
  (planFeatures || []).forEach((mod) => {
    (mod.pages || []).forEach((pg) => {
      allPages.push({ module: mod.name, name: pg.name, price: pg.price });
    });
  });

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 12px",
          background: "#EDE9FE",
          color: "#5B21B6",
          border: "none",
          borderRadius: 8,
          fontWeight: 700,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <FaFile style={{ fontSize: 11 }} />
        {planPages ?? 0} Pages
        {open ? (
          <FaChevronUp style={{ fontSize: 10 }} />
        ) : (
          <FaChevronDown style={{ fontSize: 10 }} />
        )}
      </button>

      {open && allPages.length > 0 && (
        <div
          style={{
            marginTop: 8,
            background: "#FAFBFF",
            border: "1px solid #E5E9F2",
            borderRadius: 10,
            overflow: "hidden",
            animation: "fadeSlide 0.2s ease",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#EDE9FE" }}>
                {["Module", "Page", "Price"].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      padding: "7px 12px",
                      textAlign: i === 2 ? "right" : "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#5B21B6",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allPages.map((pg, i) => (
                <tr
                  key={i}
                  style={{
                    background: i % 2 === 0 ? "#fff" : "#F8F9FD",
                    borderBottom: "1px solid #EEF0F6",
                  }}
                >
                  <td
                    style={{
                      padding: "6px 12px",
                      fontSize: 12,
                      color: "#7B849C",
                      fontWeight: 600,
                    }}
                  >
                    {pg.module}
                  </td>
                  <td
                    style={{
                      padding: "6px 12px",
                      fontSize: 12,
                      color: "#1B1B29",
                      fontWeight: 700,
                    }}
                  >
                    {pg.name}
                  </td>
                  <td
                    style={{
                      padding: "6px 12px",
                      fontSize: 12,
                      color: "#06D6A0",
                      fontWeight: 800,
                      textAlign: "right",
                    }}
                  >
                    ${pg.price}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && allPages.length === 0 && (
        <p style={{ marginTop: 6, fontSize: 12, color: "#A0ABC0", fontStyle: "italic" }}>
          No page details available.
        </p>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   SINGLE ADDON ROW  (expandable pages sub-list)
──────────────────────────────────────────────────────────── */
const AddonRow = ({ addon }) => {
  const [open, setOpen] = useState(false);
  const pages = addon.pages_list || [];

  return (
    <div
      style={{
        background: "#FAFAFA",
        border: "1px solid #E5E9F2",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {/* header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "9px 12px",
          gap: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          {/* addon name */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontWeight: 700,
              color: "#1B1B29",
              fontSize: 13,
              marginBottom: 4,
            }}
          >
            <FaPlug style={{ color: "#FF9F04", fontSize: 11 }} />
            {addon.addon_name}
          </div>

          {/* purchased date + time — exact system time */}
          <DateTimeBadges ds={addon.purchased_at} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontWeight: 800, color: "#06D6A0", fontSize: 13 }}>
            {addon.price !== "N/A" ? `$${addon.price}` : ""}
          </span>

          {/* expand arrow only when sub-pages exist */}
          {pages.length > 0 && (
            <button
              onClick={() => setOpen((o) => !o)}
              style={{
                background: "#F0F0FF",
                border: "none",
                borderRadius: 6,
                padding: "4px 8px",
                cursor: "pointer",
                color: "#604BE8",
                fontWeight: 700,
                fontSize: 11,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {open ? <FaChevronUp /> : <FaChevronDown />}
            </button>
          )}
        </div>
      </div>

      {/* expanded pages table */}
      {open && pages.length > 0 && (
        <div
          style={{
            borderTop: "1px solid #E5E9F2",
            background: "#F8F9FD",
            animation: "fadeSlide 0.2s ease",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#EDE9FE" }}>
                <th
                  style={{
                    padding: "6px 12px",
                    textAlign: "left",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#5B21B6",
                    textTransform: "uppercase",
                  }}
                >
                  Page
                </th>
                <th
                  style={{
                    padding: "6px 12px",
                    textAlign: "right",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#5B21B6",
                    textTransform: "uppercase",
                  }}
                >
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {pages.map((pg, i) => {
                const name =
                  typeof pg === "string"
                    ? pg.replace(/page_/g, "").replace(/_/g, " ")
                    : pg.name || pg.pageId || "—";
                const price =
                  typeof pg === "object" && pg.price !== undefined
                    ? `$${pg.price}`
                    : "";
                return (
                  <tr
                    key={i}
                    style={{
                      background: i % 2 === 0 ? "#fff" : "#F8F9FD",
                      borderBottom: "1px solid #EEF0F6",
                    }}
                  >
                    <td
                      style={{
                        padding: "5px 12px",
                        fontSize: 12,
                        color: "#1B1B29",
                        fontWeight: 700,
                        textTransform: "capitalize",
                      }}
                    >
                      {name}
                    </td>
                    <td
                      style={{
                        padding: "5px 12px",
                        fontSize: 12,
                        color: "#06D6A0",
                        fontWeight: 800,
                        textAlign: "right",
                      }}
                    >
                      {price}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
──────────────────────────────────────────────────────────── */
const Subscribe = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`/api/superadmin/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.subscriptions) setSubscriptions(res.data.subscriptions);
      } catch (err) {
        console.error("Error fetching subscriptions:", err);
        setError("Failed to fetch subscription data. Please check your connection.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = subscriptions.filter((sub) => {
    const q = searchTerm.toLowerCase();
    const match =
      sub.tenant_name?.toLowerCase().includes(q) ||
      sub.email?.toLowerCase().includes(q);
    if (activeTab === "with_addons")
      return match && sub.addons && sub.addons.length > 0;
    return match;
  });

  /* ── render ── */
  return (
    <div
      style={{
        padding: "40px",
        backgroundColor: "#F3F6FB",
        minHeight: "100vh",
        fontFamily: "'Outfit', 'Gilroy', sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        @import url('https://fonts.cdnfonts.com/css/gilroy-bold');

        @keyframes fadeSlide {
          from { opacity:0; transform:translateY(-6px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes spin {
          0%  { transform:rotate(0deg); }
          100%{ transform:rotate(360deg); }
        }

        .premium-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .premium-table th {
          background: #06D6A0;
          color: #ffffff;
          font-weight: 600;
          font-size: 15px;
          text-transform: capitalize;
          letter-spacing: 0.3px;
          padding: 16px 24px;
          text-align: left;
          border: none;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .premium-table th:first-child {
          border-top-left-radius: 10px;
        }
        .premium-table th:last-child {
          border-top-right-radius: 10px;
        }
        .premium-table td {
          padding: 16px 24px;
          color: #4A4A5A;
          font-size: 14px;
          font-weight: 500;
          border-bottom: 1px solid #F1F3F8;
          transition: all 0.2s ease;
          vertical-align: middle;
        }
        .premium-table tr:nth-child(odd) td {
          background: #ffffff;
        }
        .premium-table tr:nth-child(even) td {
          background: #FAFBFD;
        }
        .premium-table tr:hover td {
          background: #F1F4F9;
        }
        .premium-table tr:last-child td {
          border-bottom: none;
        }
      `}</style>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 28 }}>

        {/* ── Top row: Back button left, Search right ── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          {/* Back button – top left corner */}
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#fff",
              border: "1.5px solid #E5E9F2",
              padding: "9px 20px",
              borderRadius: 12,
              cursor: "pointer",
              color: "#604BE8",
              fontWeight: 700,
              fontSize: 14,
              boxShadow: "0 2px 10px rgba(96,75,232,0.08)",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "#EDE9FE";
              e.currentTarget.style.transform = "translateX(-3px)";
              e.currentTarget.style.borderColor = "#604BE8";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.borderColor = "#E5E9F2";
            }}
          >
            <FaArrowLeft style={{ fontSize: 13 }} />
            Back
          </button>

          {/* Search – top right */}
          <div style={{ position: "relative" }}>
            <FaSearch
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#7B849C",
                fontSize: 13,
              }}
            />
            <input
              type="text"
              placeholder="Search tenants / email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "11px 16px 11px 40px",
                borderRadius: 12,
                border: "1.5px solid #E5E9F2",
                width: 260,
                fontSize: 14,
                outline: "none",
                backgroundColor: "#fff",
                color: "#1B1B29",
                fontWeight: 500,
              }}
              onFocus={(e) => (e.target.style.borderColor = "#604BE8")}
              onBlur={(e) => (e.target.style.borderColor = "#E5E9F2")}
            />
          </div>
        </div>

        {/* ── Title row ── */}
        <div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: "#1B1B29" }}>
            Subscriptions
          </h1>
          <p style={{ margin: "6px 0 0", color: "#7B849C", fontSize: 14, fontWeight: 500 }}>
            All tenant plans, companies, branches and add-on purchases.
          </p>
        </div>

      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {[
          { key: "all", label: "All Subscribers" },
          { key: "with_addons", label: "With Add-ons" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: "9px 22px",
              background: activeTab === t.key ? "#604BE8" : "transparent",
              color: activeTab === t.key ? "#fff" : "#4A4A5A",
              border: "none",
              borderRadius: 10,
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 14,
              transition: "all 0.2s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 280,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: "4px solid #E5E9F2",
              borderTopColor: "#604BE8",
              borderRadius: "50%",
              animation: "spin 0.9s linear infinite",
            }}
          />
        </div>
      )}

      {/* ── Error ── */}
      {!loading && error && (
        <div
          style={{
            padding: "18px 24px",
            background: "#FFF0F0",
            color: "#D93025",
            borderRadius: 12,
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && !error && filtered.length === 0 && (
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            background: "#fff",
            borderRadius: 16,
          }}
        >
          <FaLayerGroup style={{ fontSize: 46, color: "#DCE3EE", marginBottom: 14 }} />
          <h3 style={{ margin: "0 0 6px", color: "#1B1B29", fontWeight: 700 }}>
            No subscriptions found
          </h3>
          <p style={{ margin: 0, color: "#7B849C", fontSize: 14 }}>
            Try adjusting your search or tab filter.
          </p>
        </div>
      )}

      {/* ── Table ── */}
      {!loading && !error && filtered.length > 0 && (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 6px 24px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              className="premium-table"
              style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}
            >
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Validity</th>
                  <th>Price</th>
                  <th>Pages</th>
                  <th>Companies</th>
                  <th>Branches</th>
                  <th>Add-ons</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((sub, idx) => {
                  const compNames = sub.companies_names || [];
                  const branchNames = sub.branches_names || [];
                  const hasAddons = sub.addons && sub.addons.length > 0;

                  return (
                    <tr key={idx}>
                      {/* Organization */}
                      <td style={{ verticalAlign: "top", minWidth: 160 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 9,
                              background: "#F3F0FE",
                              color: "#604BE8",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 15,
                              flexShrink: 0,
                            }}
                          >
                            <FaBuilding />
                          </div>
                          <div
                            style={{ fontWeight: 700, color: "#1B1B29", fontSize: 14 }}
                          >
                            {sub.tenant_name || "Unknown"}
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td
                        style={{
                          verticalAlign: "top",
                          color: "#7B849C",
                          fontSize: 13,
                          fontWeight: 500,
                          minWidth: 180,
                        }}
                      >
                        {sub.email || "—"}
                      </td>

                      {/* Plan name  +  LAST UPDATED date/time underneath */}
                      <td style={{ verticalAlign: "top", minWidth: 170 }}>
                        <Pill bg="#EDE9FE" color="#5B21B6">
                          <FaTag style={{ fontSize: 10 }} />
                          {sub.subscription_plan || "Basic"}
                        </Pill>
                        <Pill bg={sub.billing_cycle && sub.billing_cycle !== 'Monthly' ? '#D1FAE5' : '#E0E7FF'} color={sub.billing_cycle && sub.billing_cycle !== 'Monthly' ? '#065F46' : '#3730A3'} style={{ marginLeft: 5 }}>
                          {sub.billing_cycle || "Monthly"}
                        </Pill>
                        {/* Always shows the latest edit time (falls back to created_at if never edited) */}
                        <div style={{ marginTop: 6 }}>
                          <span style={{ fontSize: 10, color: "#A0ABC0", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 3 }}>
                            Subscribed
                          </span>
                          <DateTimeBadges ds={sub.subscription_updated_at || sub.created_at} />
                        </div>
                      </td>

                      {/* Validity column — from/to dates for discount plans */}
                      <td style={{ verticalAlign: "top", minWidth: 200 }}>
                        {sub.billing_cycle && sub.billing_cycle !== 'Monthly' && sub.subscription_start_date ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div>
                              <span style={{ fontSize: 10, color: "#A0ABC0", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 3 }}>
                                From
                              </span>
                              <DateTimeBadges ds={sub.subscription_start_date} />
                            </div>
                            <div style={{ marginTop: 4 }}>
                              <span style={{ fontSize: 10, color: "#A0ABC0", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 3 }}>
                                To
                              </span>
                              <DateTimeBadges ds={sub.subscription_end_date} />
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#A0ABC0", fontSize: 12, fontStyle: "italic" }}>N/A</span>
                        )}
                      </td>

                      {/* Price */}
                      <td style={{ verticalAlign: "top" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            fontWeight: 700,
                            color: "#1B1B29",
                            fontSize: 14,
                          }}
                        >
                          <FaMoneyBillWave
                            style={{ color: "#06D6A0", fontSize: 13 }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>
                              {sub.plan_price !== undefined && sub.plan_price !== null
                                ? `$${sub.billing_cycle && sub.billing_cycle !== 'Monthly' && sub.yearly_discount_applied ? Number(sub.yearly_discount_applied).toFixed(2) : sub.plan_price}`
                                : "N/A"}
                            </span>
                            {sub.billing_cycle && sub.billing_cycle !== 'Monthly' && sub.yearly_discount_applied > 0 && (
                              <span style={{ fontSize: 10, color: "#059669", fontWeight: 600 }}>
                                ({sub.billing_cycle})
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Pages – expandable */}
                      <td style={{ verticalAlign: "top", minWidth: 140 }}>
                        <PagesExpand
                          planFeatures={sub.plan_features}
                          planPages={sub.plan_pages}
                        />
                      </td>

                      {/* Companies */}
                      <td style={{ verticalAlign: "top", minWidth: 140 }}>
                        <div
                          style={{ display: "flex", flexDirection: "column", gap: 5 }}
                        >
                          {compNames.length > 0 ? (
                            compNames.map((name, ci) => (
                              <Pill key={ci} bg="#E0F2FE" color="#0369A1">
                                <FaBuilding style={{ fontSize: 10 }} />
                                {name}
                              </Pill>
                            ))
                          ) : (
                            <span
                              style={{
                                color: "#A0ABC0",
                                fontSize: 12,
                                fontStyle: "italic",
                              }}
                            >
                              No companies
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Branches */}
                      <td style={{ verticalAlign: "top", minWidth: 140 }}>
                        <div
                          style={{ display: "flex", flexDirection: "column", gap: 5 }}
                        >
                          {branchNames.length > 0 ? (
                            branchNames.map((name, bi) => (
                              <Pill key={bi} bg="#FDF2F8" color="#9D174D">
                                <FaCodeBranch style={{ fontSize: 10 }} />
                                {name}
                              </Pill>
                            ))
                          ) : (
                            <span
                              style={{
                                color: "#A0ABC0",
                                fontSize: 12,
                                fontStyle: "italic",
                              }}
                            >
                              No branches
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Add-ons – each one expandable with its own date/time */}
                      <td style={{ verticalAlign: "top", minWidth: 220 }}>
                        {hasAddons ? (
                          <div
                            style={{ display: "flex", flexDirection: "column", gap: 8 }}
                          >
                            {sub.addons.map((addon, ai) => (
                              <AddonRow key={ai} addon={addon} />
                            ))}
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "#A0ABC0",
                              fontSize: 12,
                              fontStyle: "italic",
                            }}
                          >
                            No add-ons
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscribe;
